/**
 * ReanalyseControl — "read this email again" from the launch form.
 *
 * Three states (idle / working / failed) over ONE injected callback, so the
 * control is host-neutral and fully testable without a network. `LaunchForm`
 * mounts it under the client card whenever the host supplies `onReanalyse`;
 * the add-in supplies the real one in Phase 5, ora never does (AD-9).
 *
 * This story owns the CONTROL only. The server-side discard-and-re-derive is
 * `picks-adoption`'s `reanalyze_email`; nothing here creates a second
 * extraction path or a second `email_analysis` write site (design Constraint 9).
 *
 * Two failure modes are designed against, and both are asserted by
 * `reanalyse.test.tsx`:
 *
 *  1. **Double-fire.** A second press while the first is in flight would start
 *     two concurrent re-analyses against the same email — two MLflow records,
 *     two racing merges into one values record, and a last-writer-wins result
 *     nobody can explain. The guard is a REF, not the `disabled` attribute:
 *     a programmatic `click()` (and a fast double-click before React commits)
 *     reaches the handler regardless of what the DOM says.
 *  2. **A silently emptied form.** On failure the previous values must still be
 *     there and the failure must be stated — an empty form after a press looks
 *     exactly like a successful re-analysis that found nothing. That is why the
 *     action-list drop in `LaunchForm` is SUCCESS-ONLY, and why a rejected
 *     promise lands here as rendered text rather than an unhandled rejection.
 */

import { useCallback, useId, useRef, useState } from "react";
import { TYPE_BODY } from "~/common/workflows/paneTypography";

/**
 * What the host's re-analysis reports back.
 *
 * A REJECTED promise and `{ ok: false }` are treated identically — a host may
 * either throw or report. Anything else (including `void`) is success.
 */
export interface ReanalyseResult {
  ok: boolean;
  /** Shown on the form when `ok` is false. */
  error?: string;
}

export const REANALYSE_LABEL = "Re-analyse this email";
export const REANALYSE_WORKING_LABEL = "Re-analysing…";
/** Why Start is greyed out while a re-analysis is replacing the form's values. */
export const REANALYSE_BLOCKS_START_REASON =
  "Re-analysing this email… Start is available once the refreshed values are on the form.";
export const REANALYSE_DEFAULT_ERROR =
  "Re-analysis failed. The values below are the ones you started with — nothing was changed.";

function messageOf(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === "string" && error) return error;
  return REANALYSE_DEFAULT_ERROR;
}

export interface ReanalyseControlProps {
  /** Injected by `LaunchForm`, which wraps the host's callback with the
   *  success-only action-list drop. */
  onReanalyse: () => Promise<ReanalyseResult | void>;
  disabled?: boolean;
}

export default function ReanalyseControl({
  onReanalyse,
  disabled = false,
}: ReanalyseControlProps) {
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const uid = useId().replace(/:/g, "");
  const statusId = `launch-reanalyse-status-${uid}`;

  const press = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setWorking(true);
    setError(null);
    try {
      const result = await onReanalyse();
      if (result && result.ok === false) {
        setError(result.error ?? REANALYSE_DEFAULT_ERROR);
      }
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      inFlight.current = false;
      setWorking(false);
    }
  }, [onReanalyse]);

  return (
    <div className="flex flex-col gap-0.5" data-testid="launch-reanalyse">
      <button
        type="button"
        disabled={disabled || working}
        aria-busy={working || undefined}
        aria-describedby={working || error ? statusId : undefined}
        onClick={() => {
          void press();
        }}
        // `touch-target` (app/tailwind.css), NOT the `min-h-11 sm:min-h-0`
        // this briefly carried. That version keyed the 44px floor to viewport
        // WIDTH, and the Outlook task pane is ~320-450px — permanently below
        // `sm:` — so the floor never lifted there and this 2xs chip rendered
        // as a box three lines tall (reported 2026-09-21). `touch-target`
        // keys on `(pointer: coarse)` instead: the chip stays small on the
        // desktop AND in the pane, and only grows for an actual finger.
        className="touch-target self-start rounded-md border border-primary-gray-light-300 px-2 py-0.5 text-2xs font-medium text-primary-gray-light-700 disabled:opacity-60"
        data-testid="launch-reanalyse-button"
      >
        {working ? REANALYSE_WORKING_LABEL : REANALYSE_LABEL}
      </button>
      {/* Screen-reader only (story 28633): the button already reads
          "Re-analysing…", so a visible second line said the same thing twice.
          The live region stays so assistive tech still hears it start. */}
      {working ? (
        <p
          id={statusId}
          role="status"
          className="sr-only"
          data-testid="launch-reanalyse-working"
        >
          {REANALYSE_WORKING_LABEL}
        </p>
      ) : null}
      {!working && error ? (
        <p
          id={statusId}
          role="alert"
          className={`${TYPE_BODY} text-primary-error-600`}
          data-testid="launch-reanalyse-error"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}
